/**
 * Geographic utilities for course leg calculations.
 */

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/**
 * Haversine distance between two points in nautical miles.
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number} distance in nautical miles
 */
export function distanceNm(lat1, lng1, lat2, lng2) {
  const R = 3440.065; // Earth radius in nautical miles
  const dLat = (lat2 - lat1) * DEG_TO_RAD;
  const dLng = (lng2 - lng1) * DEG_TO_RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Initial bearing from point 1 to point 2 in degrees (0-360).
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number} bearing in degrees
 */
export function bearingDeg(lat1, lng1, lat2, lng2) {
  const dLng = (lng2 - lng1) * DEG_TO_RAD;
  const y = Math.sin(dLng) * Math.cos(lat2 * DEG_TO_RAD);
  const x =
    Math.cos(lat1 * DEG_TO_RAD) * Math.sin(lat2 * DEG_TO_RAD) -
    Math.sin(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.cos(dLng);
  return ((Math.atan2(y, x) * RAD_TO_DEG) + 360) % 360;
}

/**
 * Compass bearing FROM a start/finish mark TO its offset line, honoring the
 * configured offset angle.
 *
 * `offsetAngle` is what the published bundle carries for a vessel/offset line:
 *   - `null` / `undefined` → the wind-derived default (start: downwind of the
 *     mark; finish: upwind of the mark). Published bundles store the semantic
 *     'downwind'/'upwind' keywords as null (see normalizeOffsetAngle in
 *     CourseCards), so this is the common case.
 *   - a number (or numeric string) → an explicit "bearing from mark to vessel",
 *     i.e. the projection bearing itself. This matches both the editor input
 *     ("Bearing from mark to vessel") and the app's live placement
 *     (active_navigation_dashboard resolveStartMark / finish projection).
 *   - the keywords 'downwind' / 'upwind' are also accepted (WIP bundles that
 *     haven't been through publish normalization may still carry them).
 *
 * @param {number|string|null|undefined} offsetAngle
 * @param {number} windBearing  compass bearing the wind blows FROM
 * @param {boolean} isStart      true = start line, false = finish line
 * @returns {number|null} bearing in degrees (0-360), or null when windBearing
 *   is unavailable
 */
export function offsetLineBearing(offsetAngle, windBearing, isStart) {
  if (typeof windBearing !== 'number') return null;
  const downwind = (windBearing + 180) % 360; // direction wind blows TO
  const upwind = ((windBearing % 360) + 360) % 360; // into the wind
  const fallback = isStart ? downwind : upwind;
  if (offsetAngle == null || offsetAngle === '') return fallback;
  if (offsetAngle === 'downwind') return downwind;
  if (offsetAngle === 'upwind') return upwind;
  const num = Number(offsetAngle);
  if (Number.isFinite(num)) return ((num % 360) + 360) % 360;
  return fallback;
}

/**
 * Auto-derive the orientation bearing for a 'fixed_mark' start/finish line — the
 * line that sits AT a single fixed mark and is crossed perpendicular to the
 * course's approach/departure direction.
 *
 * The returned value is a "mark → line" axis bearing in the same sense
 * offsetLineBearing/offsetLineBearingDeg consume it: the line is drawn
 * perpendicular to it. For a START it is the direction boats sail away from the
 * mark (markA → first onward course mark); for a FINISH it is the final-approach
 * direction (last prior course mark → markA). Robust whether markA is itself a
 * course mark or a separate dedicated start/finish mark.
 *
 * @param {string[]} markIds        ordered course mark ids
 * @param {Object} markLookup       { markId: [lat, lng] }
 * @param {string} markAId          the single fixed mark the line sits on
 * @param {boolean} isStart         true = start line, false = finish line
 * @returns {number|null} bearing in degrees (0-360), or null when undeterminable
 */
export function fixedMarkLineBearing(markIds, markLookup, markAId, isStart) {
  if (!Array.isArray(markIds) || markIds.length === 0 || !markAId) return null;
  const a = markLookup[markAId];
  if (!a) return null;
  if (isStart) {
    // Direction boats sail away from the start: markA → first onward mark that
    // isn't markA itself (handles markA being the first course mark or separate).
    for (let i = 0; i < markIds.length; i++) {
      if (markIds[i] === markAId) continue;
      const o = markLookup[markIds[i]];
      if (o) return bearingDeg(a[0], a[1], o[0], o[1]);
    }
    return null;
  }
  // Finish: final-approach direction → markA from the last prior course mark.
  for (let i = markIds.length - 1; i >= 0; i--) {
    if (markIds[i] === markAId) continue;
    const p = markLookup[markIds[i]];
    if (p) return bearingDeg(p[0], p[1], a[0], a[1]);
  }
  return null;
}

/**
 * Calculate leg details for a course's mark sequence.
 * @param {string[]} markIds
 * @param {Object} lookup  - { markId: [lat, lng] }
 * @returns {{ from: string, to: string, distNm: number, bearing: number }[]}
 */
export function legDetails(markIds, lookup) {
  const legs = [];
  for (let i = 0; i < markIds.length - 1; i++) {
    const posA = lookup[markIds[i]];
    const posB = lookup[markIds[i + 1]];
    if (!posA || !posB) {
      legs.push({ from: markIds[i], to: markIds[i + 1], distNm: null, bearing: null });
      continue;
    }
    legs.push({
      from: markIds[i],
      to: markIds[i + 1],
      distNm: distanceNm(posA[0], posA[1], posB[0], posB[1]),
      bearing: bearingDeg(posA[0], posA[1], posB[0], posB[1]),
    });
  }
  return legs;
}
